import {
  buildSelectorFromOffsets,
  buildSelectorFromSlices,
  locateSelector,
} from "@norkron/web-elaborate-editor";
import type { FlashOptions } from "@norkron/web-page-layout";
import type { AnnotationDraft, Comment } from "../../api/types";
import { type AnchorState, hashQuote } from "../../lib/anchoring";
import { draftFromSelector } from "../../lib/surfaceComment";
import {
  revealSurfaceRects,
  type ScrollIntent,
} from "../../lib/scrollMotion";

/**
 * `readCanvasAnchors` — the byte-space equivalent of `lib/anchoring`, for the
 * CANVAS read surface.
 *
 * The DOM read view resolves a stored comment by walking text nodes, building a
 * `Range` and wrapping it in `<span class="docz-comment">`. A `<canvas>` has no
 * text nodes, no `Range` and no spans: the only coordinate that exists is
 * `(blockOrd, block-relative UTF-8 byte offset)` — the SAME coordinate the docz
 * IR, `pl_layout`'s geometry index and the W3C selector already speak. So the
 * read canvas does not re-derive anchoring; it resolves the stored selector
 * straight into that coordinate and asks the engine for the rects.
 *
 * RULE ZERO — the locator is `@norkron/web-elaborate-editor`'s `locateSelector`,
 * the ONE entry point (it routes single-block vs cross-block itself, from the
 * selector, so this file cannot get that branch wrong), and the write side is its
 * `buildSelectorFromOffsets` / `buildSelectorFromSlices`. What lives here is only
 * the product glue those do not own:
 *   · the `block_ref` → ordinal mapping across the TWO grammars that exist in
 *     stored data (`stampBlocks`'s `b{index}` from the DOM read view, and the
 *     transport block id the canvas EDITOR writes),
 *   · the whitespace-insensitive rescue `resolveAnnotation` performs on the DOM
 *     side, so a comment does not orphan on the canvas that would have survived
 *     on the DOM,
 *   · the `exact` vs `moved` classification, which needs elaborate's
 *     `quote_hash` (the lib returns `exact`/`shifted` and knows nothing about
 *     affixes or the hash).
 *
 * ⚠ THE CROSS-BLOCK REGRESSION THIS CLOSES. A comment authored across blocks
 * saved and re-anchored correctly in EDIT mode and came back `orphan` HERE, with
 * no highlight — the feature half-working, which reads to a reviewer as the
 * feature being broken. Two causes, both now gone: the search was a per-block
 * `indexOf` (a cross-block quote carries `BLOCK_JOIN`, which no single block's
 * text contains, so every block missed), and {@link ReadCanvasAnchor} was
 * STRUCTURALLY single-block, so even a correct resolve had nowhere to put the
 * other bands.
 */

const UTF8 = new TextEncoder();
const UTF8_DEC = new TextDecoder();
/** The affix window `buildSelectorFromOffsets` uses — restated for the classifier. */
const AFFIX = 32;

const byteLen = (s: string): number => UTF8.encode(s).length;

/** One document block as the read canvas sees it: the plain text `pl_layout`
 *  laid out, plus the transport id when the server supplied one. */
export interface ReadBlock {
  readonly id?: string;
  readonly text: string;
}

/**
 * ONE block's painted contribution to a comment's anchor.
 *
 * The canvas equivalent of `canvasCommentBands.CommentBand` (which speaks block
 * IDS, because the editing surface has them) resolved down to the ORDINAL the
 * engine's `rangeRects` takes. A comment anchored across blocks is, in the docz
 * IR, N blocks each carrying `Mark{.comment, commentId}` over its own sub-range —
 * so N bands is what that costs on a surface with no DOM spans.
 */
export interface AnchorBand {
  readonly blockOrd: number;
  /** Block-relative UTF-8 byte offsets. `start === end` for a blank block that
   *  is INSIDE the range but has no glyphs — a real part of the range with no ink
   *  in it, which is not the same thing as an absence. */
  readonly start: number;
  readonly end: number;
}

/** A resolved comment highlight in the canvas's own coordinate. */
export interface ReadCanvasAnchor {
  /** The comment id, stringified (the id form every highlight API takes). */
  readonly commentId: string;
  /** The comment type — the highlight's colour source (rich-comments §12). */
  readonly type: string;
  /**
   * EVERY block the comment covers, in document order. A single-block comment is
   * ONE band — not a different shape — so a painter that iterates this is correct
   * for both cases with no special-casing.
   */
  readonly bands: readonly AnchorBand[];
  /** The FIRST band's ordinal. Kept so anything that predates cross-block anchors
   *  still reads a meaningful coordinate instead of breaking. */
  readonly blockOrd: number;
  /** The FIRST band's block-relative UTF-8 byte offsets. See {@link blockOrd}. */
  readonly start: number;
  readonly end: number;
  /** `exact` only when the stored bytes AND the affix context AND the quote hash
   *  all still agree — the same bar `resolveAnnotation` sets on the DOM side. */
  readonly state: "exact" | "moved";
}

/**
 * WHERE each resolved anchor sits on this surface — the canvas twin of
 * `lib/anchoring.documentPointOfRange`, and the review rail's "Document order".
 *
 * The coordinate is taken from the anchor the resolver just produced, so a note
 * whose quote MOVED reports where it is NOW rather than the `block_ref` it was
 * written against. For a cross-block anchor the position is its FIRST band: a
 * range starts where it starts, and the rail sorts by start.
 *
 * `offset` is a UTF-8 BYTE offset, where the DOM surface published a CHARACTER
 * offset. That is safe and is not a silent unit change: `lib/reviewOrder` only
 * ever compares offsets WITHIN one block on one surface, and both units are
 * monotone in position, so the ordering is identical. It also happens to match
 * the units of the RECORDED fallback coordinate (`block_ref` + `start`), which
 * the DOM surface never did.
 *
 * Orphans are deliberately absent: an orphan has no position, and inventing one
 * would put a note the reader cannot see into the middle of the reading order.
 */
export function anchorPointsOf(
  anchors: readonly ReadCanvasAnchor[],
): Record<number, { blockOrd: number; offset: number }> {
  const points: Record<number, { blockOrd: number; offset: number }> = {};
  for (const a of anchors) {
    const id = Number(a.commentId);
    if (!Number.isFinite(id)) continue;
    const first = a.bands[0];
    if (!first) continue;
    points[id] = { blockOrd: first.blockOrd, offset: first.start };
  }
  return points;
}

/** The total bytes a comment's bands cover — the "how wide is this anchor"
 *  question, which for a cross-block anchor is not `end - start`. */
export function anchorSpan(anchor: ReadCanvasAnchor): number {
  let total = 0;
  for (const band of anchor.bands) total += band.end - band.start;
  return total;
}

/**
 * The slice of `@norkron/web-page-layout`'s `CanvasSurfaceHandle` the anchor
 * jump needs. Structural, and generic over the rect type, so this module never
 * imports the engine — that is what lets the jump be tested in a runtime with no
 * Canvas 2D context (happy-dom has none, so `<ReadCanvas>` can never reach a
 * live surface headlessly). Only the `animate` bit of the engine's
 * `SurfaceScrollOptions` is named here; the rest (block, margin, ifNeeded) stays
 * at the engine's defaults, which is the point — this file decides intent, not
 * geometry.
 */
export interface ScrollableSurface<Rect = unknown> {
  rangeRects(blockOrd: number, start: number, end: number): readonly Rect[];
  scrollTo(rects: readonly Rect[], opts?: { readonly animate?: boolean }): boolean;
  /**
   * One-shot bloom over `rects`: `color` is painted at full alpha and faded to
   * transparent, revealing the resting highlight beneath. Returns a cancel fn.
   *
   * OPTIONAL on purpose — an engine build without it must still SCROLL rather
   * than throw, so the jump degrades to what it was before the bloom existed.
   * Under `prefers-reduced-motion` the engine schedules ZERO animation frames:
   * the wash is painted statically, held for `durationMs`, then released.
   */
  flashHighlights?(rects: readonly Rect[], opts: FlashOptions): () => void;
}

/** The product's bloom decision for ONE anchor. `null` = do not bloom. */
export interface AnchorFlash {
  /** The peak wash — `lib/canvasCommentColors`'s `CommentPaint.bloom`. */
  readonly color: string;
  /** Defaults to the engine's own default; `<ReadCanvas>` passes the DOM
   *  keyframe's 1.4s explicitly so the parity is stated at the call site. */
  readonly durationMs?: number;
  /** Receives the engine's cancel handle, so a LATER focus can supersede this
   *  bloom instead of compositing two washes over each other. */
  readonly onStart?: (cancel: () => void) => void;
}

export interface ReadAnchorResolution {
  readonly anchors: readonly ReadCanvasAnchor[];
  /** Per-comment-id anchor state, INCLUDING orphans — the same map shape
   *  `useDoczCanvas` publishes, so the review rail is fed identically. */
  readonly states: Record<number, AnchorState>;
}

/**
 * Map a stored `block_ref` onto a block ordinal, or `-1` when no block owns it.
 *
 * TWO grammars exist in production data and both must resolve:
 *  · `b{index}` — what `lib/anchoring.stampBlocks` writes onto the server HTML,
 *    so every comment filed from the DOM read view carries it;
 *  · the transport block id — what `<CanvasElaborateEditor>.placeCommentMark`
 *    writes, because the canvas editor has real block ids.
 * An explicit id ALWAYS wins over the index reading, so a server that happens to
 * name a block "b0" cannot silently re-route its comments to ordinal 0.
 */
export function blockOrdForRef(
  blocks: readonly ReadBlock[],
  blockRef: string,
): number {
  if (blockRef.length === 0) return -1;
  const byId = blocks.findIndex((b) => b.id === blockRef);
  if (byId >= 0) return byId;
  const m = /^b(\d+)$/.exec(blockRef);
  if (!m) return -1;
  const ord = Number(m[1]);
  return ord >= 0 && ord < blocks.length ? ord : -1;
}

/** Whitespace-stripped haystack + a map back to raw char indices. */
function stripWhitespace(raw: string): { stripped: string; rawIndexOf: number[] } {
  const rawIndexOf: number[] = [];
  let stripped = "";
  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i];
    if (ch === undefined || /\s/.test(ch)) continue;
    stripped += ch;
    rawIndexOf.push(i);
  }
  return { stripped, rawIndexOf };
}

/**
 * The whitespace-INSENSITIVE rescue `resolveAnnotation` performs on the DOM
 * side: strip all whitespace from both haystack and needle, match, then map the
 * hit back to raw char offsets → byte offsets. Rescues the glued-text render
 * (inter-word spaces dropped) and a quote that captured a newline. Always a
 * NON-exact hit.
 */
function locateWhitespaceInsensitive(
  text: string,
  quote: string,
): { start: number; end: number } | null {
  const needle = quote.replace(/\s+/g, "");
  if (needle.length === 0) return null;
  const { stripped, rawIndexOf } = stripWhitespace(text);
  const idx = stripped.indexOf(needle);
  if (idx < 0) return null;
  const rawStart = rawIndexOf[idx];
  const rawLast = rawIndexOf[idx + needle.length - 1];
  if (rawStart === undefined || rawLast === undefined) return null;
  return {
    start: byteLen(text.slice(0, rawStart)),
    end: byteLen(text.slice(0, rawLast + 1)),
  };
}

/** Does the text around `[start,end)` still carry the stored affix context? */
function affixesAgree(
  text: string,
  start: number,
  end: number,
  prefix: string,
  suffix: string,
): boolean {
  const bytes = UTF8.encode(text);
  const before = UTF8_DEC.decode(bytes.subarray(0, start));
  const after = UTF8_DEC.decode(bytes.subarray(end));
  const gotPrefix = before.slice(Math.max(0, before.length - AFFIX));
  const gotSuffix = after.slice(0, AFFIX);
  return (
    (prefix === "" || gotPrefix.endsWith(prefix) || prefix.endsWith(gotPrefix)) &&
    (suffix === "" || gotSuffix.startsWith(suffix) || suffix.startsWith(gotSuffix))
  );
}

function quoteAt(text: string, start: number, end: number): string {
  const bytes = UTF8.encode(text);
  return UTF8_DEC.decode(bytes.subarray(start, end));
}

interface Hit {
  readonly bands: readonly AnchorBand[];
  readonly exactOffsets: boolean;
}

/**
 * The whitespace-insensitive rescue, block by block. Only ever a SINGLE band: a
 * quote whose whitespace has diverged from the render (the glued-text render, a
 * captured newline) is by definition not going to be matched structurally, and a
 * guess spread across blocks would be a guess about where a range ENDS.
 */
function rescueWhitespaceInsensitive(
  blocks: readonly ReadBlock[],
  quote: string,
  preferred: number,
): Hit | null {
  const order = preferred >= 0 ? [preferred, ...blocks.map((_, i) => i)] : blocks.map((_, i) => i);
  for (const ord of order) {
    const ws = locateWhitespaceInsensitive(blocks[ord]?.text ?? "", quote);
    if (ws) return { bands: [{ blockOrd: ord, ...ws }], exactOffsets: false };
  }
  return null;
}

/**
 * Resolve every ROOT comment against the live block text. Replies (`parent_id`)
 * and quote-less comments are skipped — exactly as the DOM engine skips them.
 *
 * The locate itself is upstream (`locateSelector`): it reads the selector, routes
 * single-block vs cross-block from it, and returns one slice per covered block in
 * document order. Which is the whole point — the branch that used to live here
 * was written wrong, and a cross-block quote can only ever miss a per-block
 * `indexOf`.
 *
 * `exact` requires all three of: the stored byte range still spells the quote,
 * the affix context still agrees, and the quote hash matches. Anything weaker
 * that still finds the quote is `moved`; a quote that is gone is `orphan` and
 * contributes no highlight.
 *
 * For a CROSS-BLOCK quote the affix/hash bar is measured against the FIRST band
 * only. The hash is over the joined quote, which no single block's text spells,
 * and the affixes are (by construction, upstream) the ~32 chars before the first
 * slice and after the last INSIDE THEIR OWN BLOCKS. So the honest test is: did
 * the upstream walk find the run at the recorded starting offset, and does the
 * prefix still agree where it was taken.
 */
export function resolveReadCanvasAnchors(
  blocks: readonly ReadBlock[],
  comments: readonly Comment[],
): ReadAnchorResolution {
  const anchors: ReadCanvasAnchor[] = [];
  const states: Record<number, AnchorState> = {};

  // The id vocabulary the upstream locator works in. A read block may carry no
  // transport id at all (the server HTML read view), so the ordinal grammar
  // `stampBlocks` writes is synthesised for it — and the selector is then named
  // in that SAME vocabulary below, so an anchor on its recorded block still reads
  // as `exact` rather than being demoted to a document-wide scan.
  const ids = blocks.map((b, i) => b.id ?? `b${i}`);
  const located = blocks.map((b, i) => ({ id: ids[i] as string, text: b.text }));

  for (const c of comments) {
    if (c.parent_id !== null || c.quote === "") continue;

    const preferred = blockOrdForRef(blocks, c.block_ref);
    const hit =
      locateBands(located, c, preferred >= 0 ? (ids[preferred] as string) : c.block_ref) ??
      rescueWhitespaceInsensitive(blocks, c.quote, preferred);

    const first = hit?.bands[0];
    if (!hit || !first) {
      states[c.id] = "orphan";
      continue;
    }

    const text = blocks[first.blockOrd]?.text ?? "";
    const crossBlock = hit.bands.length > 1;
    const exact =
      hit.exactOffsets &&
      first.blockOrd === preferred &&
      affixesAgree(text, first.start, first.end, c.prefix, c.suffix) &&
      (crossBlock || hashQuote(quoteAt(text, first.start, first.end)) === c.quote_hash);
    const state: "exact" | "moved" = exact ? "exact" : "moved";
    states[c.id] = state;
    anchors.push({
      commentId: String(c.id),
      type: c.type,
      bands: hit.bands,
      blockOrd: first.blockOrd,
      start: first.start,
      end: first.end,
      state,
    });
  }

  return { anchors, states };
}

/** The stored comment → upstream's locator → ordinal bands. */
function locateBands(
  located: readonly { id: string; text: string }[],
  c: Comment,
  blockId: string,
): Hit | null {
  const hit = locateSelector(located, {
    blockId,
    exactQuote: c.quote,
    prefix: c.prefix,
    suffix: c.suffix,
    start: c.start,
    end: c.end,
  });
  if (!hit) return null;
  const bands: AnchorBand[] = [];
  for (const s of hit.slices) {
    const ord = located.findIndex((b) => b.id === s.blockId);
    if (ord < 0) continue;
    bands.push({ blockOrd: ord, start: s.start, end: s.end });
  }
  if (bands.length === 0) return null;
  return { bands, exactOffsets: hit.state === "exact" };
}

/**
 * Turn a settled canvas selection into the durable `AnnotationDraft` the comment
 * composer posts — the canvas counterpart of
 * `lib/anchoring.selectorFromCurrentSelection`.
 *
 * The `block_ref` deliberately uses the `b{ord}` INDEX grammar, not the
 * transport id: that is what `stampBlocks` writes on the DOM read view, so a
 * comment filed from either read surface resolves on the other. (The canvas
 * EDITOR writes transport ids instead — a pre-existing divergence, reported, not
 * introduced here.)
 */
export function draftFromCanvasSelection(
  blocks: readonly ReadBlock[],
  sel: {
    readonly blockOrd: number;
    readonly start: number;
    readonly end: number;
    /** The engine's per-block slices, present when the range CROSSES blocks. A
     *  reader must be able to comment on two paragraphs from the read surface
     *  too, not only from the editor. */
    readonly blocks?: readonly {
      readonly blockOrd: number;
      readonly start: number;
      readonly end: number;
    }[];
  },
): AnnotationDraft | null {
  const slices = sel.blocks && sel.blocks.length > 1 ? sel.blocks : null;
  const s = slices
    ? buildSelectorFromSlices({
        slices: slices.map((x) => ({
          blockId: `b${x.blockOrd}`,
          text: blocks[x.blockOrd]?.text ?? "",
          start: x.start,
          end: x.end,
        })),
      })
    : blocks[sel.blockOrd]
      ? buildSelectorFromOffsets({
          blockId: `b${sel.blockOrd}`,
          text: blocks[sel.blockOrd]?.text ?? "",
          start: sel.start,
          end: sel.end,
        })
      : null;
  if (!s) return null;
  // The persisted (start, end) pair stays block-relative to `block_ref` — the
  // one contract, stated once in `lib/surfaceComment.draftFromSelector`.
  return draftFromSelector(s);
}

/**
 * The canvas "jump to the anchored text" seam, extracted so it is testable
 * WITHOUT a Canvas 2D context (happy-dom has none, so `<ReadCanvas>` itself can
 * never reach a live surface in a unit test).
 *
 * It is the whole of what the imperative handle does: find the resolved anchor,
 * ask the surface for its page rects, reveal them with the behavior the caller's
 * `intent` resolves to, and then BLOOM them. `null` means the quote is no longer
 * placeable — the caller must SAY so (a toast), never pretend the jump happened.
 *
 * THE BLOOM IS THE OTHER HALF OF THE JUMP, and it was missing. On the DOM read
 * view a Review-Note click ran `lib/anchoring.flashAnnotation`, which washed the
 * span in `--ela-anchor-exact` and faded it back over 1.4s
 * (`@keyframes ela-anno-bloom`). Scrolling alone drops a reviewer on a page of
 * justified text with no indication of WHICH words they were sent to — the jump
 * looks like it did nothing.
 *
 * ORDER IS LOAD-BEARING: scroll first, bloom second. A bloom issued before the
 * scroll fades over text the reviewer cannot see yet, and by the time they arrive
 * a third of it is already gone. A scroll the surface could NOT reach suppresses
 * the bloom entirely — there is nothing on screen to bloom, and the caller is
 * about to toast a miss.
 *
 * The motion itself stays the engine's, exactly as `lib/scrollMotion` keeps the
 * scroll the engine's: nothing here schedules a frame or a timer (asserted in
 * `readCanvasAnchors.flash.test.ts`), and the `prefers-reduced-motion` gate is
 * NOT re-queried product-side — the component that runs the tween is the one that
 * must refuse to run it.
 */
export function focusAnchorOnSurface<Rect>(
  surface: ScrollableSurface<Rect> | null,
  anchors: readonly ReadCanvasAnchor[],
  commentId: number | string,
  intent: ScrollIntent = "navigate",
  flashFor?: (anchor: ReadCanvasAnchor) => AnchorFlash | null,
): AnchorState | null {
  if (!surface) return null;
  const id = String(commentId);
  const anchor = anchors.find((a) => a.commentId === id);
  if (!anchor) return null;
  // EVERY band, not just the first. A reviewer sent to a comment that spans a
  // paragraph and a table must be shown the range, not its opening words — and a
  // bloom over one band of six reads as the jump having landed in the wrong place.
  const rects: Rect[] = [];
  for (const band of anchor.bands) {
    rects.push(...surface.rangeRects(band.blockOrd, band.start, band.end));
  }
  if (!revealSurfaceRects(surface, rects, intent)) return null;

  const flash = flashFor?.(anchor) ?? null;
  if (flash && surface.flashHighlights) {
    const cancel = surface.flashHighlights(rects, {
      color: flash.color,
      ...(flash.durationMs !== undefined ? { durationMs: flash.durationMs } : {}),
    });
    flash.onStart?.(cancel);
  }
  return anchor.state;
}
